import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Grid,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import api from '@/services/authService';

interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  role: 'admin' | 'user';
  registrationStatus: 'pending' | 'approved' | 'rejected';
  status?: 'pending' | 'approved' | 'rejected';
  phone?: string;
  address?: string | any;
  dateOfBirth?: string;
  age?: number;
  approvedAt?: string;
  createdAt: string;
}

interface UserBalance {
  currency: 'EUR' | 'USD';
  balance: number;
}

interface CreditCard {
  id: number;
  cardLastFour: string;
  cardholderName: string;
  cardType: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

interface Transaction {
  id: number;
  transactionType: 'deposit' | 'withdrawal' | 'transfer';
  amount: number;
  currency: 'EUR' | 'USD';
  status: 'pending' | 'completed' | 'failed';
  description: string | null;
  referenceId: string;
  completedAt: string | null;
  createdAt: string;
}

interface LoanApplication {
  id: number;
  loanType: string;
  amount: number;
  currency: 'EUR' | 'USD';
  paymentMethod: 'account' | 'card';
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  approvedAt: string | null;
  createdAt: string;
}

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

interface CompleteUserDetails {
  user: User;
  balances: UserBalance[];
  creditCards: CreditCard[];
  transactions: Transaction[];
  loanApplications: LoanApplication[];
  notifications: Notification[];
  kycStatus: {
    hasPersonalInfo: boolean;
    hasBalances: boolean;
    hasCreditCards: boolean;
    hasTransactions: boolean;
    hasLoanApplications: boolean;
  };
}

const AdvancedUserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  // Dialog states
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [sendNotificationDialogOpen, setSendNotificationDialogOpen] = useState(false);

  // Form states
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    age: 18,
    dateOfBirth: '',
    role: 'user' as 'admin' | 'user',
    address: ''
  });

  const [editUserForm, setEditUserForm] = useState({
    username: '',
    email: '',
    fullName: '',
    age: 18,
    dateOfBirth: '',
    role: 'user' as 'admin' | 'user',
    address: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [notificationForm, setNotificationForm] = useState({
    subject: '',
    message: ''
  });

  const [selectedMenuUser, setSelectedMenuUser] = useState<User | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [completeUserDetails, setCompleteUserDetails] = useState<CompleteUserDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      const usersFromResponse = response.data?.data?.users ?? response.data?.users ?? [];
      
      // Map snake_case to camelCase for compatibility
      const mappedUsers = Array.isArray(usersFromResponse) ? usersFromResponse.map((user: any) => ({
        ...user,
        registrationStatus: user.registration_status || user.registrationStatus,
        dateOfBirth: user.date_of_birth || user.dateOfBirth,
        fullName: user.full_name || user.fullName,
        approvedAt: user.approved_at || user.approvedAt,
        createdAt: user.created_at || user.createdAt
      })) : [];
      
      setUsers(mappedUsers);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle bulk actions
  const handleBulkAction = async (action: 'approve' | 'reject' | 'delete') => {
    try {
      // Filter users based on action
      const usersToProcess = users.filter(user => {
        if (action === 'delete') {
          return selectedUsers.includes(user.id);
        } else if (action === 'approve') {
          // Only approve users with pending status
          return selectedUsers.includes(user.id) && user.registrationStatus === 'pending';
        } else if (action === 'reject') {
          // Only reject users with pending status
          return selectedUsers.includes(user.id) && user.registrationStatus === 'pending';
        }
        return false;
      });

      const promises = usersToProcess.map(user => {
        if (action === 'delete') {
          return api.delete(`/admin/users/${user.id}`);
        } else if (action === 'approve') {
          return api.put(`/admin/users/${user.id}/approve`);
        } else if (action === 'reject') {
          return api.put(`/admin/users/${user.id}/reject`, { reason: 'Rejected by admin' });
        }
        return Promise.resolve();
      });
      
      await Promise.all(promises);
      setSelectedUsers([]);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${action} users`);
    }
  };

  // Handle Add User
  const handleAddUser = async () => {
    // Validate form
    const errors: Record<string, string> = {};
    
    if (!newUserForm.username || newUserForm.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(newUserForm.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    }
    
    if (!newUserForm.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUserForm.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    if (!newUserForm.password || newUserForm.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (!newUserForm.fullName || newUserForm.fullName.length < 2) {
      errors.fullName = 'Full name must be at least 2 characters';
    }
    
    if (!newUserForm.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setFormErrors({});
    
    try {
      await api.post('/admin/users', newUserForm);
      setAddUserDialogOpen(false);
      setNewUserForm({
        username: '',
        email: '',
        password: '',
        fullName: '',
        age: 18,
        dateOfBirth: '',
        role: 'user',
        address: ''
      });
      setFormErrors({});
      fetchUsers();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error?.details?.[0]?.msg ||
                      err.response?.data?.message ||
                      'Failed to add user';
      setError(errorMsg);
      setFormErrors({ general: errorMsg });
    }
  };

  // Handle View Details
  const handleViewDetails = async (user: User) => {
    setSelectedMenuUser(user);
    setUserMenuAnchor(null);
    setLoadingDetails(true);
    setViewDetailsDialogOpen(true);
    
    try {
      const response = await api.get(`/admin/users/${user.id}/details`);
      setCompleteUserDetails(response.data?.data ?? response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Helper function to safely format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  // Helper function to safely format date with time
  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  // Handle Approve User
  const handleApproveUser = async (userId: number) => {
    try {
      const response = await api.put(`/admin/users/${userId}/approve`);
      setUserMenuAnchor(null);
      fetchUsers();
      // Show success message
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve user');
    }
  };

  // Handle Reject User
  const handleRejectUser = async (userId: number) => {
    try {
      await api.put(`/admin/users/${userId}/reject`, { reason: 'Rejected by admin' });
      setUserMenuAnchor(null);
      fetchUsers();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject user');
    }
  };

  // Handle Edit User
  const handleEditUser = async () => {
    if (!selectedMenuUser) return;
    try {
      await api.put(`/admin/users/${selectedMenuUser.id}`, editUserForm);
      setEditUserDialogOpen(false);
      setUserMenuAnchor(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedMenuUser(user);
    // Handle address which might be an object or string
    const addressValue = typeof user.address === 'string'
      ? (() => {
          try {
            const parsedAddress = JSON.parse(user.address);
            return `${parsedAddress.street || ''}, ${parsedAddress.city || ''}, ${parsedAddress.state || ''} ${parsedAddress.zipCode || ''}, ${parsedAddress.country || ''}`;
          } catch {
            return user.address;
          }
        })()
      : user.address
        ? `${user.address.street || ''}, ${user.address.city || ''}, ${user.address.state || ''} ${user.address.zipCode || ''}, ${user.address.country || ''}`
        : '';
    
    setEditUserForm({
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      age: user.age || 18,
      dateOfBirth: user.dateOfBirth || '',
      role: user.role,
      address: addressValue
    });
    setEditUserDialogOpen(true);
    setUserMenuAnchor(null);
  };

  // Handle Send Notification
  const handleSendNotification = async () => {
    if (!selectedMenuUser) return;
    try {
      await api.post(`/admin/users/${selectedMenuUser.id}/notify`, notificationForm);
      setSendNotificationDialogOpen(false);
      setUserMenuAnchor(null);
      setNotificationForm({ subject: '', message: '' });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send notification');
    }
  };

  // Handle Delete User
  const handleDeleteUser = async (userId: number) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      setUserMenuAnchor(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  // Handle menu open/close
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setSelectedMenuUser(user);
    setUserMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Get status chip
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'approved':
        return <Chip label="Approved" color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" icon={<CancelIcon />} />;
      default:
        return <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Advanced User Management
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setAddUserDialogOpen(true)}
                sx={{ mr: 1 }}
              >
                Add User
              </Button>
              {selectedUsers.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => handleBulkAction('approve')}
                    sx={{ mr: 1 }}
                  >
                    Approve Selected
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleBulkAction('delete')}
                  >
                    Delete Selected
                  </Button>
                </>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Total Users: {users.length}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(users.map(u => u.id));
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([...selectedUsers, user.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2 }}>
                            {user.fullName && typeof user.fullName === 'string'
                              ? user.fullName.charAt(0)
                              : 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="body1">
                              {user.fullName || 'Unknown User'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              @{user.username || 'unknown'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role || 'user'}
                          color={user.role === 'admin' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{getStatusChip(user.registrationStatus)}</TableCell>
                      <TableCell>
                        {user.createdAt
                          ? (() => {
                              try {
                                return new Date(user.createdAt).toLocaleDateString();
                              } catch {
                                return 'N/A';
                              }
                            })()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {user.registrationStatus === 'pending' && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => handleApproveUser(user.id)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                onClick={() => handleRejectUser(user.id)}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedMenuUser && handleViewDetails(selectedMenuUser)}>
          View Details
        </MenuItem>
        {selectedMenuUser && selectedMenuUser.registrationStatus === 'pending' && (
          <>
            <MenuItem onClick={() => selectedMenuUser && handleApproveUser(selectedMenuUser.id)}>
              Approve User
            </MenuItem>
            <MenuItem onClick={() => selectedMenuUser && handleRejectUser(selectedMenuUser.id)}>
              Reject User
            </MenuItem>
          </>
        )}
        <MenuItem onClick={() => selectedMenuUser && openEditDialog(selectedMenuUser)}>
          Edit User
        </MenuItem>
        <MenuItem onClick={() => setSendNotificationDialogOpen(true)}>
          Send Notification
        </MenuItem>
        <MenuItem
          onClick={() => selectedMenuUser && handleDeleteUser(selectedMenuUser.id)}
          sx={{ color: 'error.main' }}
        >
          Delete User
        </MenuItem>
      </Menu>

      {/* Add User Dialog */}
      <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={newUserForm.username}
                onChange={(e) => {
                  setNewUserForm({ ...newUserForm, username: e.target.value });
                  setFormErrors({ ...formErrors, username: '' });
                }}
                error={!!formErrors.username}
                helperText={formErrors.username || 'Only letters, numbers, and underscores (min 3 chars)'}
                inputProps={{ pattern: '^[a-zA-Z0-9_]+$' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newUserForm.email}
                onChange={(e) => {
                  setNewUserForm({ ...newUserForm, email: e.target.value });
                  setFormErrors({ ...formErrors, email: '' });
                }}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newUserForm.password}
                onChange={(e) => {
                  setNewUserForm({ ...newUserForm, password: e.target.value });
                  setFormErrors({ ...formErrors, password: '' });
                }}
                error={!!formErrors.password}
                helperText={formErrors.password || 'Minimum 6 characters'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={newUserForm.fullName}
                onChange={(e) => {
                  setNewUserForm({ ...newUserForm, fullName: e.target.value });
                  setFormErrors({ ...formErrors, fullName: '' });
                }}
                error={!!formErrors.fullName}
                helperText={formErrors.fullName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={newUserForm.age}
                onChange={(e) => setNewUserForm({ ...newUserForm, age: parseInt(e.target.value) || 18 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={newUserForm.dateOfBirth}
                onChange={(e) => {
                  setNewUserForm({ ...newUserForm, dateOfBirth: e.target.value });
                  setFormErrors({ ...formErrors, dateOfBirth: '' });
                }}
                error={!!formErrors.dateOfBirth}
                helperText={formErrors.dateOfBirth}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUserForm.role}
                  label="Role"
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value as 'admin' | 'user' })}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={newUserForm.address}
                onChange={(e) => setNewUserForm({ ...newUserForm, address: e.target.value })}
              />
            </Grid>
          </Grid>
          {formErrors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formErrors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddUserDialogOpen(false);
            setFormErrors({});
          }}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained">Add User</Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialogOpen} onClose={() => setViewDetailsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">User Details - KYC Verification</Typography>
            {loadingDetails && <Typography variant="body2" color="text.secondary">Loading...</Typography>}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography>Loading user details...</Typography>
            </Box>
          ) : completeUserDetails ? (
            <Box sx={{ mt: 2 }}>
              {/* User Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Avatar sx={{ width: 80, height: 80, mr: 3, fontSize: 32 }}>
                  {completeUserDetails.user.fullName?.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h5">{completeUserDetails.user.fullName || 'Unknown'}</Typography>
                  <Typography variant="body1" color="text.secondary">@{completeUserDetails.user.username || 'unknown'}</Typography>
                  <Box sx={{ mt: 1 }}>{getStatusChip(completeUserDetails.user.registrationStatus || 'pending')}</Box>
                </Box>
              </Box>

              {/* KYC Verification Status */}
              <Card sx={{ mb: 3, bgcolor: completeUserDetails.user.registrationStatus === 'approved' ? '#e8f5e9' : '#fff3e0' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color={completeUserDetails.user.registrationStatus === 'approved' ? 'success' : 'warning'} />
                    KYC Verification Status
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {completeUserDetails.kycStatus.hasPersonalInfo ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                        <Typography variant="body2">Personal Info</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {completeUserDetails.kycStatus.hasBalances ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                        <Typography variant="body2">Balances</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {completeUserDetails.kycStatus.hasCreditCards ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                        <Typography variant="body2">Credit Cards</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {completeUserDetails.kycStatus.hasTransactions ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                        <Typography variant="body2">Transactions</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {completeUserDetails.kycStatus.hasLoanApplications ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                        <Typography variant="body2">Loan Applications</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Personal Information */}
              <Typography variant="h6" gutterBottom>Personal Information</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Email</Typography>
                      </Box>
                      <Typography variant="body1">{completeUserDetails.user.email || 'Not provided'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Role</Typography>
                      </Box>
                      <Typography variant="body1">{completeUserDetails.user.role || 'user'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                      </Box>
                      <Typography variant="body1">
                        {completeUserDetails.user.dateOfBirth || 'Not provided'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Age: {completeUserDetails.user.age || 'Not provided'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Account Created</Typography>
                      </Box>
                      <Typography variant="body1">
                        {completeUserDetails.user.createdAt ? new Date(completeUserDetails.user.createdAt).toLocaleDateString() : 'Not provided'}
                      </Typography>
                      {completeUserDetails.user.approvedAt && (
                        <Typography variant="body2" color="text.secondary">
                          Approved: {new Date(completeUserDetails.user.approvedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                {completeUserDetails.user.address && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Address</Typography>
                        <Typography variant="body1">
                          {typeof completeUserDetails.user.address === 'string'
                            ? (() => {
                                try {
                                  const parsedAddress = JSON.parse(completeUserDetails.user.address);
                                  return `${parsedAddress.street || ''}, ${parsedAddress.city || ''}, ${parsedAddress.state || ''} ${parsedAddress.zipCode || ''}, ${parsedAddress.country || ''}`;
                                } catch {
                                  return completeUserDetails.user.address;
                                }
                              })()
                            : `${completeUserDetails.user.address?.street || ''}, ${completeUserDetails.user.address?.city || ''}, ${completeUserDetails.user.address?.state || ''} ${completeUserDetails.user.address?.zipCode || ''}, ${completeUserDetails.user.address?.country || ''}`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              {/* Balances */}
              <Typography variant="h6" gutterBottom>Account Balances</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {completeUserDetails.balances.length > 0 ? (
                  completeUserDetails.balances.map((balance, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Card>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {balance.currency} Balance
                          </Typography>
                          <Typography variant="h5" color="primary">
                            {balance.currency} {parseFloat(balance.balance).toFixed(2)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">No balance information available</Alert>
                  </Grid>
                )}
              </Grid>

              {/* Credit Cards */}
              <Typography variant="h6" gutterBottom>Credit Cards</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {completeUserDetails.creditCards.length > 0 ? (
                  completeUserDetails.creditCards.map((card, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {card.cardType} ••••• {card.cardLastFour}
                            </Typography>
                            {card.isDefault && <Chip label="Default" size="small" color="primary" />}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Cardholder: {card.cardholderName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Expires: {String(card.expiryMonth).padStart(2, '0')}/{card.expiryYear}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              label={card.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={card.isActive ? 'success' : 'default'}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">No credit cards registered</Alert>
                  </Grid>
                )}
              </Grid>

              {/* Recent Transactions */}
              <Typography variant="h6" gutterBottom>Recent Transactions (Last 20)</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {completeUserDetails.transactions.length > 0 ? (
                  completeUserDetails.transactions.slice(0, 5).map((transaction, idx) => (
                    <Grid item xs={12} key={idx}>
                      <Card>
                        <CardContent sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {transaction.transactionType.toUpperCase()} - {transaction.referenceId}
                              </Typography>
                              <Typography variant="body1">
                                {transaction.currency} {parseFloat(transaction.amount).toFixed(2)}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Chip
                                label={transaction.status}
                                size="small"
                                color={
                                  transaction.status === 'completed' ? 'success' :
                                  transaction.status === 'failed' ? 'error' : 'warning'
                                }
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                {new Date(transaction.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">No transactions found</Alert>
                  </Grid>
                )}
              </Grid>

              {/* Loan Applications */}
              <Typography variant="h6" gutterBottom>Loan Applications</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {completeUserDetails.loanApplications.length > 0 ? (
                  completeUserDetails.loanApplications.map((loan, idx) => (
                    <Grid item xs={12} key={idx}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {loan.loanType}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {loan.currency} {parseFloat(loan.amount).toFixed(2)} via {loan.paymentMethod}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Applied: {new Date(loan.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Chip
                                label={loan.status}
                                size="small"
                                color={
                                  loan.status === 'approved' ? 'success' :
                                  loan.status === 'rejected' ? 'error' : 'warning'
                                }
                              />
                              {loan.rejectionReason && (
                                <Typography variant="body2" color="error" sx={{ display: 'block', mt: 1 }}>
                                  Reason: {loan.rejectionReason}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">No loan applications found</Alert>
                  </Grid>
                )}
              </Grid>

              {/* Recent Notifications */}
              <Typography variant="h6" gutterBottom>Recent Notifications (Last 10)</Typography>
              <Grid container spacing={2}>
                {completeUserDetails.notifications.length > 0 ? (
                  completeUserDetails.notifications.map((notification, idx) => (
                    <Grid item xs={12} key={idx}>
                      <Card>
                        <CardContent sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {notification.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {notification.message}
                              </Typography>
                            </Box>
                            <Box sx={{ ml: 2, textAlign: 'right' }}>
                              <Chip
                                label={notification.isRead ? 'Read' : 'Unread'}
                                size="small"
                                color={notification.isRead ? 'default' : 'primary'}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                {formatDateTime(notification.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">No notifications found</Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : selectedMenuUser && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ width: 80, height: 80, mr: 3, fontSize: 32 }}>
                  {selectedMenuUser.fullName?.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h5">{selectedMenuUser.fullName || 'Unknown'}</Typography>
                  <Typography variant="body1" color="text.secondary">@{selectedMenuUser.username || 'unknown'}</Typography>
                  <Box sx={{ mt: 1 }}>{getStatusChip(selectedMenuUser.registrationStatus || 'pending')}</Box>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Email</Typography>
                      </Box>
                      <Typography variant="body1">{selectedMenuUser.email || 'Not provided'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Role</Typography>
                      </Box>
                      <Typography variant="body1">{selectedMenuUser.role || 'user'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                      </Box>
                      <Typography variant="body1">
                        {selectedMenuUser.dateOfBirth
                          ? (typeof selectedMenuUser.dateOfBirth === 'string'
                              ? selectedMenuUser.dateOfBirth
                              : new Date(selectedMenuUser.dateOfBirth).toLocaleDateString())
                          : 'Not provided'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Age: {selectedMenuUser.age || 'Not provided'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">Account Created</Typography>
                      </Box>
                      <Typography variant="body1">
                        {selectedMenuUser.createdAt ? new Date(selectedMenuUser.createdAt).toLocaleDateString() : 'Not provided'}
                      </Typography>
                      {selectedMenuUser.approvedAt && (
                        <Typography variant="body2" color="text.secondary">
                          Approved: {new Date(selectedMenuUser.approvedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                {selectedMenuUser.address && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Address</Typography>
                        <Typography variant="body1">
                          {typeof selectedMenuUser.address === 'string'
                            ? (() => {
                                try {
                                  const parsedAddress = JSON.parse(selectedMenuUser.address);
                                  return `${parsedAddress.street || ''}, ${parsedAddress.city || ''}, ${parsedAddress.state || ''} ${parsedAddress.zipCode || ''}, ${parsedAddress.country || ''}`;
                                } catch {
                                  return selectedMenuUser.address;
                                }
                              })()
                            : `${selectedMenuUser.address?.street || ''}, ${selectedMenuUser.address?.city || ''}, ${selectedMenuUser.address?.state || ''} ${selectedMenuUser.address?.zipCode || ''}, ${selectedMenuUser.address?.country || ''}`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onClose={() => setEditUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={editUserForm.username}
                onChange={(e) => setEditUserForm({ ...editUserForm, username: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={editUserForm.fullName}
                onChange={(e) => setEditUserForm({ ...editUserForm, fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={editUserForm.age}
                onChange={(e) => setEditUserForm({ ...editUserForm, age: parseInt(e.target.value) || 18 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editUserForm.dateOfBirth}
                onChange={(e) => setEditUserForm({ ...editUserForm, dateOfBirth: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editUserForm.role}
                  label="Role"
                  onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value as 'admin' | 'user' })}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={editUserForm.address}
                onChange={(e) => setEditUserForm({ ...editUserForm, address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={sendNotificationDialogOpen} onClose={() => setSendNotificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Notification</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subject"
            value={notificationForm.subject}
            onChange={(e) => setNotificationForm({ ...notificationForm, subject: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            value={notificationForm.message}
            onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendNotificationDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendNotification} variant="contained">Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedUserManagement;
